import classNames from 'classnames'
import imgOther from 'legacy/lut/assets/img/icon-other.png'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IFormCategories } from 'legacy/lut/classes/models/IFormCategories'
import { IFormPartners } from 'legacy/lut/classes/models/IFormPartners'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { companyStoreService } from 'legacy/lut/classes/services/CompanyStoreService'
import { partnerDataStoreService } from 'legacy/lut/classes/services/PartnerDataStoreService'
import { GoogleMap } from 'legacy/lut/components/shared/googleMaps/GooglePoint'
import { WidgetAttachmentFieldEditor } from 'legacy/lut/components/widgets/editors/WidgetTableEditor/WidgetTableFields/WidgetAttachmentFieldEditor'
import { Dropdown } from 'legacy/lut/library/dropdown'
import { IState } from 'legacy/lut/store'
import { lookupGetStateOld, setNextLookupState } from 'legacy/lut/store'
import { ILookupState } from 'legacy/lut/store/state'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import sanitize from 'sanitize-html'

import { Translation } from 'cng-common/features/translations/components/translation'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Checkbox } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { IDataProviderFilter, Logic, Operator } from 'core-components/types'
import { toFormat } from 'core/classes/helpers/DateHelper'
import { arrayUnique } from 'core/classes/helpers/Helper'
import { calculateFileLink } from 'core/classes/helpers/saveFiles'
import { EditableArea } from 'core/components/shared/ui/EditableArea'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'

import { Gallery } from './editors/Gallery'
import { ItemsController } from './editors/ItemsController'
import BusinessHours from './partnerPreview/BusinessHours'
import Payments from './partnerPreview/Payments'
import POSImages from './partnerPreview/POSImages'
import { PartnerPreviewMode } from './PartnerPreviewMode'
import { usePartnerImages } from './usePartnerImages'
import { useSpecialization } from './useSpecialization'

import styles from './PartnerPreview.module.scss'

interface IProps {
  widgetParams: IObjectAny
  PartnerId?: string
  editable?: boolean
  WidgetTableEditor?: any
  NeedUpdateInMetalocator?: boolean
}

const coverageItems = [{ value: 'Kilometers' }, { value: 'Meters' }]

const timeUnits = [
  { key: 'Minutes', label: 'Minutes' },
  { key: 'Hours', label: 'Hours' },
  { key: 'Days', label: 'Days' },
  { key: 'Weeks', label: 'Weeks' },
]

const setSource = (categories: IFormCategories[] = [], groupName: string) => {
  if (!categories.length) {
    return []
  }
  return categories
    .filter((item: any) => item.tag_group.toLowerCase().replace(/\s+/g, '') === groupName)
    .sort((a: any, b: any) => a.id - b.id)
}

const setSourceGroups = (categories: IFormCategories[] = [], groups: string[]) => {
  if (!categories.length) {
    return []
  }
  return categories
    .filter(
      (item: IFormCategories) =>
        groups.indexOf(item.tag_group.toLowerCase().replace(/\s+/g, '')) > -1
    )
    .sort((a: any, b: any) => a.id - b.id)
}

const PartnerPreview = ({ widgetParams, editable, PartnerId, NeedUpdateInMetalocator }: IProps) => {
  const [tagIdList, setTagIdList] = useState<number[]>([])
  const [software, setSoftware] = useState<IFormCategories[]>([])
  const [other, setOther] = useState<IFormCategories[]>([])
  const [paymentTypes, setPaymentTypes] = useState<IFormCategories[]>([])
  const [areasOfFocus, setAreasOfFocus] = useState<IFormCategories[]>([])
  const [serviceOffered, setServiceOffered] = useState<IFormCategories[]>([])
  const [currentCompany, setCurrentCompany] = useState<IFormPartners>()
  const [branches, setBranches] = useState<IFormProfile[]>([])
  const [categories, setCategories] = useState<IFormCategories[]>()
  const [noData, setNoData] = useState<boolean>(false)
  const [currentDay, setCurrentDay] = useState(-1)
  const [isForced, setIsForced] = useState(false)
  const [needUpdate, setNeedUpdate] = useState<boolean>(false)
  const location = useLocation()
  const navigate = useNavigate()
  const paymentTypeSource = useMemo(() => setSource(categories, 'paymenttypes'), [categories])
  const industriesSource = useMemo(() => setSource(categories, 'industries'), [categories])
  const industriesCoveredSource = useMemo(
    () => setSource(categories, 'hppartnerfirstspecializations'),
    [categories]
  )
  const softwareSource = useMemo(() => setSource(categories, 'software'), [categories])
  const zipCodeSource = useMemo(() => {
    return ((currentCompany && currentCompany?.ZipCodes) || '').split(',').map((el: any) => {
      return { name: el }
    })
  }, [currentCompany])
  const serviceOfferedSource = useMemo(
    () =>
      setSourceGroups(categories, [
        'itproducts&services',
        'itsupport&maintenance',
        'printsolutions&supplies',
        'security',
        'networking&communications',
        'otherservices',
      ]),
    [categories]
  )
  const logoItem: any = usePartnerImages(
    PartnerId || widgetParams['PartnerProId'] || '',
    'Logo',
    isForced
  )
  const images: any = usePartnerImages(
    PartnerId || widgetParams['PartnerProId'] || '',
    'Gallery',
    isForced
  )

  const timeSource = useMemo(() => {
    return new Crud({
      fetcher: new Static(timeUnits),
      keyField: 'key',
    })
  }, [])

  const coverageSource = useMemo(() => {
    return new Crud({
      fetcher: new Static(coverageItems),
      keyField: 'value',
    })
  }, [])

  const hdlSave = async (value: any, fieldName: string) => {
    const result = {
      ...currentCompany,
      [fieldName]: fieldName === 'ZipCodes' ? (value.length ? value.join(',') : '') : value,
    }
    await handleClickApply({ ...result } as IFormPartners)
  }

  const saveCoords = async (latValue: string, lngValue: string) => {
    if (currentCompany) {
      await handleClickApply({
        ...currentCompany,
        Lat: latValue,
        Lng: lngValue,
      })
    }
  }

  const fetchCurrentCompany = async (pid: string) => {
    const crud = CrudStore.get(CrudStoreItem.PartnerAllData, 'PartnerPreview-fetchCurrentCompany')
    const result = await crud.getById({ id: pid, keyField: 'PartnerID' })
    if (!result) {
      setNoData(true)
      return
    }
    setNoData(false)

    let data = result
    if (data) {
      if (!data.EntryIdAdd) {
        const result = await partnerDataStoreService.changePartnersExt(new DataModel(data))
        if (result.rawData) {
          data = { ...result.rawData }
        }
      }
      if (!data.TagIdList) {
        data.TagIdList = ''
      }

      setTagIdList(
        arrayUnique(
          data.TagIdList.replace(/\s/g, '')
            .split(',')
            .map((item: string) => Number(item))
        )
      )
      setCurrentCompany(data)
    }
    setNeedUpdate(true)
    return
  }

  const fetchBranches = async () => {
    const topPartnerId = currentCompany && currentCompany.TopPartnerID
    const pid = PartnerId || widgetParams['PartnerProId']
    if (!topPartnerId || !pid) return
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [
        {
          operator: Operator.EQUAL,
          field: 'TopPartnerID',
          value: topPartnerId || '',
        },
        {
          operator: Operator.NOTEQUAL,
          field: 'PartnerID',
          value: pid || '',
        },
      ],
    }
    const crud = CrudStore.get(CrudStoreItem.PartnerAllData, 'PartnerPreview-fetchBranches')
    crud.setFilter(filter)
    crud.setPaging({
      page: 0,
      pageSize: 5,
    })
    const result = await crud.fetch()
    await setBranches(result.items)
    setNeedUpdate(false)
  }

  const fetchCategories = async (isForced = false) => {
    if (!categories || isForced) {
      const data = await companyStoreService.getCategories(isForced)
      setCategories([...data.items])
    }
  }

  const industriesCovered = useSpecialization(
    categories,
    tagIdList,
    'HP Partner First Specializations'
  )

  React.useEffect(() => {
    if (!categories || !tagIdList) {
      return
    }
    const group1 = categories.filter(
      (value: IFormCategories) =>
        tagIdList.includes(value.id) && value.tag_group === 'IT Products & Services'
    )
    const group2 = categories.filter(
      (value: IFormCategories) =>
        tagIdList.includes(value.id) && value.tag_group === 'IT Support & Maintenance'
    )
    const group3 = categories.filter(
      (value: IFormCategories) =>
        tagIdList.includes(value.id) && value.tag_group === 'Print Solutions & Supplies'
    )
    const group4 = categories.filter(
      (value: IFormCategories) => tagIdList.includes(value.id) && value.tag_group === 'Security'
    )
    const group5 = categories.filter(
      (value: IFormCategories) =>
        tagIdList.includes(value.id) && value.tag_group === 'Networking & Communications'
    )
    const group6 = categories.filter(
      (value: IFormCategories) =>
        tagIdList.includes(value.id) && value.tag_group === 'Other Services'
    )
    const newServiceOffered = [...group1, ...group2, ...group3, ...group4, ...group5, ...group6]
    if (JSON.stringify(newServiceOffered) !== JSON.stringify(serviceOffered)) {
      setServiceOffered(newServiceOffered)
    }

    const updateIfChanged = (setter: (value: any) => void, oldValue: any, searchValue: string) => {
      const newValue = categories.filter(
        (value: IFormCategories) => tagIdList.includes(value.id) && value.tag_group === searchValue
      )
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        setter(newValue)
      }
    }

    updateIfChanged(setAreasOfFocus, areasOfFocus, 'Industries')
    updateIfChanged(setPaymentTypes, paymentTypes, 'Payment Types')
    updateIfChanged(setSoftware, software, 'Software')
    updateIfChanged(setOther, other, 'Other')
  }, [categories, tagIdList])

  useEffect(() => {
    const pid = PartnerId || widgetParams['PartnerProId']
    if (pid) {
      fetchCurrentCompany(pid)
      fetchCategories(false)
    }
  }, [PartnerId, widgetParams['PartnerProId']])

  useEffect(() => {
    const crud = CrudStore.get(CrudStoreItem.PartnerAllData, 'PartnerPreview-subscribe')
    return crud.subscribe('change', () => {
      const pid = PartnerId || widgetParams['PartnerProId']
      if (pid) {
        fetchCurrentCompany(pid)
        fetchCategories(false)
      }
    })
  }, [])

  useEffect(() => {
    if (needUpdate) fetchBranches()
  }, [needUpdate])

  useEffect(() => {
    if (isForced) {
      setIsForced(false)
    }
  }, [isForced])

  const handleCloseEditOldBadRefactorIt = (isChanged: boolean, item: any, fieldName?: string) => {
    let newTagList = item.TagIdList.replace(/\s/g, '')
      .split(',')
      .map((item: string) => Number(item))
    if (fieldName && item[fieldName]) {
      item[fieldName].map((value: any) => newTagList.push(value.EntryId))
      newTagList = newTagList.filter(
        (elem: any, index: number, self: any) => index === self.indexOf(elem)
      )
    }
    newTagList = arrayUnique(newTagList)
    setTagIdList(newTagList)
    item.TagIdList = String(newTagList)
    handleClickApply({ ...item })
  }

  const handleCloseEdit = useCallback(
    async (newData: any[], fieldName: string) => {
      const currentTagList = (((currentCompany && currentCompany) || {}).TagIdList || '')
        .replace(/\s/g, '')
        .split(',')
        .map((item: string) => Number(item))
        .filter((val) => val !== 0)
      let currentValues: any[] = []
      switch (fieldName) {
        case 'PaymentTypes':
          currentValues = paymentTypes
          break
        case 'AreasOfFocus':
          currentValues = areasOfFocus
          break
        case 'industriesCovered':
          currentValues = industriesCovered
          break
        case 'Software':
          currentValues = software
          break
        case 'Other':
          currentValues = other
          break
        case 'ServiceOffered':
          currentValues = serviceOffered
          break
      }
      const newTagList = arrayUnique(
        currentTagList
          .filter((el) => !currentValues.find((curr) => curr.id === el))
          .concat(newData.map((data) => data.id))
      )
      setTagIdList(newTagList)
      if (currentCompany) {
        currentCompany.TagIdList = newTagList.join(',')
        await handleClickApply({ ...currentCompany })
      }
    },
    [currentCompany]
  )

  const handleClickApply = async (currentCompany: IFormPartners) => {
    currentCompany.Completement = calculateCompletement(currentCompany)
    const item = new DataModel(currentCompany)

    if (!item.isEmpty()) {
      await partnerDataStoreService.changePartnersExt(item, undefined, NeedUpdateInMetalocator)
    }
  }

  const setLocatorCompletements = (partnerId: string | undefined, completement: number) => {
    const lutState = lookupGetStateOld()
    if (
      partnerId &&
      lutState &&
      lutState.locatorCompletements &&
      lutState.locatorCompletements[partnerId] !== completement
    ) {
      setNextLookupState({
        ...lutState,
        locatorCompletements: {
          ...lutState.locatorCompletements,
          [partnerId]: completement,
        },
      })
    }
  }

  const calculateCompletement = (currentCompany: IFormPartners): number | undefined => {
    if (typeof currentCompany.TagIdList === 'string' && categories) {
      const tagIdList = arrayUnique(
        currentCompany.TagIdList.replace(/\s/g, '')
          .split(',')
          .map((item: string) => Number(item))
      )

      let countFiledFields = 0
      categories.filter(
        (value: IFormCategories) =>
          tagIdList.includes(value.id) && value.tag_group === 'Payment Types'
      ).length > 0 && countFiledFields++

      categories.filter(
        (value: IFormCategories) => tagIdList.includes(value.id) && value.tag_group === 'Industries'
      ).length > 0 && countFiledFields++

      industriesCovered.length > 0 && countFiledFields++

      categories.filter(
        (value: IFormCategories) => tagIdList.includes(value.id) && value.tag_group === 'Software'
      ).length > 0 && countFiledFields++

      categories.filter(
        (value: IFormCategories) => tagIdList.includes(value.id) && value.tag_group === 'Other'
      ).length > 0 && countFiledFields++

      const fields = ['PartnerSince', 'ResponseTimeNumber', 'Description', 'Image', 'Hours']
      fields.forEach((el: string) => {
        if (currentCompany[el]) {
          countFiledFields++
        }
      })

      const completement = Math.round((countFiledFields / 11) * 100)
      setLocatorCompletements(currentCompany.PartnerID, completement)

      return completement
    }
    return currentCompany.Completement
  }

  const pid = PartnerId || widgetParams['PartnerProId']
  if ((!currentCompany && !noData) || !categories) {
    if (!pid)
      return (
        <div style={{ textAlign: 'center' }}>
          <Translation text={'partnerpreview-unknown-partner-id'} span />
        </div>
      )
    return <Loader />
  }

  return (
    <>
      {!editable ? (
        <PartnerPreviewMode
          currentCompany={currentCompany}
          paymentTypes={paymentTypes}
          logoItem={logoItem}
          noData={noData}
          images={images}
          areasOfFocus={areasOfFocus}
          industriesCovered={industriesCovered}
          software={software}
          categories={categories}
          currentDay={currentDay}
          setCurrentDay={setCurrentDay}
          handleCloseEdit={handleCloseEditOldBadRefactorIt}
        />
      ) : (
        <div className={styles.content}>
          <main className={classNames(styles.content__body, styles.body)}>
            <div className={styles.titleBar}>
              <h3 className={styles.title}>
                {(currentCompany && currentCompany.CompanyLegalName) || 'NOT FOUND'}
              </h3>
              {currentCompany && currentCompany.ModifiedAdd ? (
                <h6>
                  Last update on {dateFormatOutcome(currentCompany.ModifiedAdd)}
                  {ordinalSuffix(currentCompany.ModifiedAdd)} |{' '}
                  {timeOutcome(currentCompany.ModifiedAdd)} by {currentCompany.ModifiedByAdd}
                </h6>
              ) : null}
            </div>
            <div>
              {editable ? (
                <div className={styles['payment-methods']}>
                  <ItemsController
                    fieldName={'PaymentTypes'}
                    handleCloseEdit={handleCloseEdit}
                    selectedValues={paymentTypes}
                    dataSource={paymentTypeSource}
                    editable={!!editable}
                    textHeader={'Payment methods'}
                  />
                </div>
              ) : paymentTypes.length ? (
                <Payments paymentTypes={paymentTypes} />
              ) : (
                <></>
              )}
            </div>
            <div>
              <ItemsController
                fieldName={'AreasOfFocus'}
                handleCloseEdit={handleCloseEdit}
                selectedValues={areasOfFocus}
                dataSource={industriesSource}
                editable={editable}
                textHeader={'partnerpreview-specialized-in'}
              />
            </div>

            <div>
              <div className={classNames(styles['partner-since'])}>
                <Translation text={'partnerpreview-hp-partner-since'} />
                {currentCompany ? (
                  <span className={styles.areaPartnerSince}>
                    <span className={styles.areaPartnerSinceText}>: </span>
                    {currentCompany.PartnerSince
                      ? toFormat(currentCompany.PartnerSince || '', 'YYYY')
                      : ''}
                  </span>
                ) : null}
              </div>
              <div className={styles.request}>
                <div>
                  <span className={styles.request__label}>
                    <Translation text={'partnerpreview-response-time'} />
                    {!editable && currentCompany && (
                      <span>
                        {' '}
                        {currentCompany.ResponseTimeNumber} {currentCompany.ResponseTimeUnits}
                      </span>
                    )}
                  </span>
                  <span>
                    {currentCompany && editable && (
                      <div className={styles.request__time}>
                        <EditableArea
                          text={
                            currentCompany.ResponseTimeNumber
                              ? `${currentCompany.ResponseTimeNumber}`
                              : ''
                          }
                          onChange={async (value: string) => hdlSave(+value, 'ResponseTimeNumber')}
                          className={styles.areaWidthTime}
                        />
                        <Dropdown
                          label={''}
                          source={timeSource}
                          classes={{
                            controlClasses: {
                              root: styles.areaWidthRange,
                            },
                          }}
                          caption="label"
                          value={''}
                          onChange={async (value: string) => {
                            currentCompany.ResponseTimeUnits = value
                            handleCloseEditOldBadRefactorIt(true, currentCompany)
                          }}
                        />
                      </div>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.areaDescription}>
              <span className={styles.request__label}>
                <Translation text={'partnerpreview-description'} />{' '}
              </span>
              {!editable && currentCompany && (
                <div
                  className={styles.typography}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(currentCompany.Description || ''),
                  }}
                />
              )}
              {currentCompany && editable && (
                <EditableArea
                  multiline
                  text={currentCompany.Description ? currentCompany.Description : ''}
                  onChange={async (value: string) => {
                    currentCompany.Description = value
                    handleCloseEditOldBadRefactorIt(true, currentCompany)
                  }}
                  className={styles.areaWidthDescription}
                />
              )}
            </div>

            <div>
              <ItemsController
                fieldName={'ServiceOffered'}
                selectedValues={serviceOffered}
                handleCloseEdit={handleCloseEdit}
                dataSource={serviceOfferedSource}
                editable={editable}
                textHeader={'partnerpreview-service-offered'}
                additionalField={'tag_group'}
              />
            </div>

            <div>
              <ItemsController
                fieldName={'industriesCovered'}
                selectedValues={industriesCovered}
                handleCloseEdit={handleCloseEdit}
                dataSource={industriesCoveredSource}
                editable={editable}
                textHeader={'partnerpreview-industries-covered'}
              />
            </div>

            <div>
              <ItemsController
                fieldName={'Software'}
                selectedValues={software}
                handleCloseEdit={handleCloseEdit}
                dataSource={softwareSource}
                editable={editable}
                textHeader={'partnerpreview-software'}
              />
            </div>
          </main>

          <aside className={classNames(styles.content__aside, styles.aside)}>
            <div className={styles.aside__logoWrapper}>
              <div
                className={classNames(styles.aside__logo, editable && styles.aside__logo__border)}
              >
                {currentCompany && logoItem && logoItem.Image && logoItem.EntryId ? (
                  <img
                    className={styles.aside__logo__image}
                    src={encodeURI(
                      calculateFileLink({
                        formId: 504,
                        entryId: logoItem.EntryId,
                        fieldName: 'Image',
                        fileName: logoItem.Image,
                        UID: logoItem.UID,
                      })
                    )}
                    alt=""
                  />
                ) : null}
                {editable && currentCompany && (
                  <>
                    <div className={styles.aside__logo__uploadIcon}>
                      <WidgetAttachmentFieldEditor
                        defaultValue={logoItem ? logoItem.Image : ''}
                        fieldName="Image"
                        fieldType="image"
                        handleCloseEdit={() => {
                          setIsForced(true)
                        }}
                        formId={504}
                        formCode={'spif-1504'}
                        entryId={logoItem ? logoItem.EntryId : undefined}
                        uid={logoItem ? logoItem.UID : undefined}
                        isLogo
                        PartnerId={PartnerId || widgetParams['PartnerProId']}
                      />
                    </div>
                    {(!logoItem || (logoItem && !logoItem.Image)) && (
                      <p className={styles.uploadTitle}>
                        <Translation text={'partnerpreview-upload-logo-here'} span />
                      </p>
                    )}
                  </>
                )}
              </div>
              {editable && (
                <p className={styles.typography}>
                  <Translation
                    text={'partnerpreview-image-should-be-386px-x-100px,-*jpg,-*.jpeg-or-*png'}
                    span
                  />
                </p>
              )}
            </div>
            <div className={styles.aside__widget}>
              {currentCompany && (
                <GoogleMap
                  className={styles.map}
                  Lat={currentCompany.Lat}
                  Lng={currentCompany.Lng}
                  Label={currentCompany.CompanyLegalName}
                  Address={currentCompany.AddressLine1}
                  City={currentCompany.City}
                  saveData={saveCoords}
                />
              )}
            </div>
            {currentCompany && (
              <div>
                <div>
                  <div className={styles['checkbox-label']}>
                    <Checkbox
                      label={<Translation span text={'partnerpreview-service-coverage'} />}
                      checked={!!currentCompany.ServiceCoverage}
                      onChange={() => {
                        hdlSave(currentCompany.ServiceCoverage ? 0 : 1, 'ServiceCoverage')
                      }}
                    />
                  </div>
                  {currentCompany.ServiceCoverage ? (
                    <div className={styles.coverageContainer}>
                      {currentCompany && (
                        <EditableArea
                          text={currentCompany.CoverageNumber || ''}
                          emptyText={'Set value'}
                          onChange={(e) => hdlSave(e, 'CoverageNumber')}
                          className={styles.areaWidthTime}
                        />
                      )}
                      {currentCompany && (
                        <Dropdown
                          label={''}
                          source={coverageSource}
                          classes={{
                            controlClasses: {
                              root: styles.areaWidthRange,
                            },
                          }}
                          caption="value"
                          value={currentCompany.CoverageUnit || ''}
                          onChange={(value: string) => hdlSave(value, 'CoverageUnit')}
                        />
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <div>
                  <div className={styles['checkbox-label']}>
                    <Checkbox
                      label={<Translation span text={'partnerpreview-zip-codes-covered'} />}
                      checked={!!currentCompany.ZipCodeCovered}
                      onChange={() => {
                        hdlSave(currentCompany.ZipCodeCovered ? 0 : 1, 'ZipCodeCovered')
                      }}
                    />
                  </div>
                  {currentCompany.ZipCodeCovered ? (
                    <div className={styles.coverageContainer}>
                      <ItemsController
                        fieldName={'ZipCodes'}
                        dataSource={zipCodeSource}
                        handleCloseEdit={hdlSave}
                        editable={editable}
                        useTextInput
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}
            <div className={classNames(styles.aside__widget, styles.business)}>
              <BusinessHours
                editable={editable}
                className={styles.business__body}
                currentDay={currentDay}
                setCurrentDay={setCurrentDay}
                currentCompany={currentCompany}
                noData={noData}
                handleCloseEdit={handleCloseEditOldBadRefactorIt}
              />
            </div>
            <div className={styles.aside__widget}>
              {!editable ? (
                <>
                  <p className={styles['list-title']}>
                    <img className={styles['list-title__icon']} src={imgOther} alt="" />
                    <Translation span text={'partnerpreview-gallery'} />
                  </p>

                  <POSImages noData={noData} images={images} />
                </>
              ) : (
                <>
                  <div className={styles['gallery']}>
                    <p className={styles['gallery-title']}>
                      <Translation span text={'partnerpreview-gallery-5-images-max'} />
                    </p>
                    <Gallery
                      imageType={'Gallery'}
                      partnerId={(currentCompany && currentCompany.PartnerID) || ''}
                      images={images}
                      onChange={() => {
                        setIsForced(true)
                      }}
                    />
                  </div>
                  <div className={styles['gallery']}>
                    <p className={styles['gallery-title']}>
                      <Translation span text={'partnerpreview-listing-page-image'} />
                    </p>
                    <Gallery
                      imageType={'Listing'}
                      partnerId={(currentCompany && currentCompany.PartnerID) || ''}
                      images={images}
                      onChange={() => {
                        setIsForced(true)
                      }}
                    />
                  </div>
                </>
              )}
              <div className={classNames(styles.iconWrapper, styles.icons)}>
                <i className={classNames(styles.icon, 'kbase kbase-omni-arrows-right')} />
              </div>
              {branches && branches.length ? (
                <div>
                  <div className={styles.request__label}>
                    <Translation span text={'partnerpreview-nearby-branches'} />
                  </div>
                  {branches.map((el: IFormPartners, index: number) => (
                    <div
                      key={index}
                      className={styles.branch}
                      onClick={() =>
                        navigate(
                          location.pathname.indexOf('/page/partner_dashboard/') === 0
                            ? `/page/partner_dashboard/${el.PartnerID}`
                            : getUrlWithParams({ pid: el.PartnerID })
                        )
                      }
                    >
                      <div className={classNames(styles.iconWrapper, styles.icons)}>
                        <i className={classNames(styles.icon, 'kbase kbase-omni-arrows-right')} />
                      </div>
                      <span className={styles.branchTextView}>
                        {el.CommunicationName || el.CompanyLegalName}
                      </span>
                    </div>
                  ))}
                  <div className={styles.branchMarginButtom} />
                </div>
              ) : (
                <></>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  )
}

const mapStateToProps = (
  state: IState & ILookupState,
  ownProps: Pick<IProps, 'PartnerId' | 'editable' | 'WidgetTableEditor' | 'widgetParams'>
) => ({
  widgetParams: ownProps.widgetParams,
  editable: ownProps.editable,
  PartnerId: ownProps.PartnerId,
  WidgetTableEditor: ownProps.WidgetTableEditor,
})

function dateFormatOutcome(date: any) {
  const dateN = new Date(date)
  const month = dateN.toLocaleString('en-us', { month: 'short' })
  const day = dateN.getDate()

  return `${month} ${day}`
}

function timeOutcome(date: any) {
  const dateN = new Date(date)
  const hour = dateN.getHours()
  const minutes = dateN.getMinutes()

  return `${hour}:${minutes}`
}

const st = 'st'
const nd = 'nd'
const rd = 'rd'
const th = 'th'

function ordinal_suffix_of(date: any) {
  const dateN = new Date(date)
  const i = dateN.getDate()
  const j = i % 10
  const k = i % 100
  if (j === 1 && k !== 11) {
    return 0
  }
  if (j === 2 && k !== 12) {
    return 1
  }
  if (j === 3 && k !== 13) {
    return 2
  }
  return 3
}

const ordinalSuffix = (date: any): any => {
  switch (ordinal_suffix_of(new Date(date))) {
    case 0: {
      return st
    }
    case 1: {
      return nd
    }
    case 2: {
      return rd
    }
    case 3: {
      return th
    }
  }
}
const PartnerPreviewConnected = connect(mapStateToProps)(PartnerPreview)
export { PartnerPreviewConnected as PartnerPreview }
